/**
 * Register the static-asset service worker (perf #5/15).
 *
 * Skipped automatically when:
 *   - The browser doesn't support service workers (older Safari /
 *     headless test environments)
 *   - We're running in development (NODE_ENV !== 'production')
 *     so live-reload doesn't fight the SW's stale cache
 *   - The page was loaded via http://localhost or 127.0.0.1 — same
 *     reason; local Railway preview builds get registered just like
 *     real prod
 *
 * Strategy: register lazily after `window.load` so the SW
 * registration doesn't compete with critical-path resource fetches.
 */

export function isServiceWorkerSupported() {
  return (
    typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
  );
}

function isLocalhost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost'
    || h === '127.0.0.1'
    || h === '[::1]'
    || /^192\.168\./.test(h)
  );
}

/**
 * Public entry point. Returns a Promise that resolves to the
 * registration object (or null if registration was skipped).
 *
 * @param {Object} [opts]
 * @param {string} [opts.scriptUrl='/service-worker.js']
 * @param {boolean} [opts.skipInDev=true]   skip when NODE_ENV !== production
 * @param {boolean} [opts.skipOnLocalhost=true]  skip on localhost / 127.x
 * @param {Function} [opts.onUpdate]   called when a new SW is waiting
 * @param {Function} [opts.onError]    called on registration errors
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function registerServiceWorker({
  scriptUrl = '/service-worker.js',
  skipInDev = true,
  skipOnLocalhost = true,
  onUpdate,
  onError,
} = {}) {
  if (!isServiceWorkerSupported()) return null;

  if (skipInDev && process.env.NODE_ENV !== 'production') return null;
  if (skipOnLocalhost && isLocalhost()) return null;

  try {
    const registration = await navigator.serviceWorker.register(scriptUrl);

    // Forward update events so the host page can show a "refresh
    // to update" banner if it wants to. We don't auto-reload because
    // that can interrupt a student mid-question.
    if (registration.waiting && typeof onUpdate === 'function') {
      onUpdate(registration);
    }

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (
          installing.state === 'installed'
          && navigator.serviceWorker.controller
        ) {
          // A new SW is waiting; old one still controls the page.
          if (typeof onUpdate === 'function') onUpdate(registration);
        }
      });
    });

    return registration;
  } catch (err) {
    if (typeof onError === 'function') {
      try { onError(err); } catch (_) { /* never blow up the app */ }
    }
    return null;
  }
}

/**
 * Unregister any active service worker. Useful for a "clear cache"
 * button or as part of logout PII-clearing flows.
 */
export async function unregisterServiceWorker() {
  if (!isServiceWorkerSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      await reg.unregister();
      return true;
    }
  } catch (_) { /* swallow */ }
  return false;
}
